import { Component, Input, OnChanges } from '@angular/core';
import { NgIf } from '@angular/common';
import { HighchartsChartModule } from 'highcharts-angular';
import Highcharts from 'highcharts';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { NoWidgetDataComponent } from '../../my-brand-screen/no-widget-data/no-widget-data.component';
import { ChannelDistribution } from '../types';

const CHANNEL_COLORS: Record<string, string> = {
    facebook: '#4A7BCA',
    instagram: '#D4076A',
    messenger: '#2154A6',
    threads: '#000000'
};

@Component({
    selector: 'app-channel-distribution',
    imports: [NgIf, HighchartsChartModule, LoaderComponent, NoWidgetDataComponent],
    templateUrl: './channel-distribution.component.html',
    styleUrl: './channel-distribution.component.scss',
})
export class ChannelDistributionComponent implements OnChanges {
    @Input() data: ChannelDistribution | undefined;
    @Input() loading = false;

    protected readonly Highcharts: typeof Highcharts = Highcharts;
    protected chartOptions: Highcharts.Options | undefined;

    ngOnChanges(): void {
        const brands = this.data?.brands;
        const hasNoDataBrand = brands?.every(b => Object.values(b.spend).every(v => v.total === 0));
        this.chartOptions = (brands?.length && !hasNoDataBrand) ? this.buildChartOptions() : undefined;
    }

    private buildChartOptions(): Highcharts.Options {
        const brands = this.data!.brands;
        const categories = brands.map(b => b.name);
        const STACK_ORDER = ['threads', 'messenger', 'instagram', 'facebook'];
        const LEGEND_ORDER = ['facebook', 'instagram', 'messenger', 'threads'];
        const channels = Object.keys(brands[0]?.spend ?? {}).sort(
            (a, b) => STACK_ORDER.indexOf(a) - STACK_ORDER.indexOf(b)
        );

        const isNoData = (brand: { spend: Record<string, { total: number }> }) =>
            Object.values(brand.spend).every(v => v.total === 0);

        const hasNoDataBrands = brands.some(isNoData);

        const series: Highcharts.SeriesColumnOptions[] = [
            ...channels.map(channel => ({
                type: 'column' as const,
                name: channel.charAt(0).toUpperCase() + channel.slice(1),
                color: CHANNEL_COLORS[channel] ?? '#888888',
                data: brands.map(brand => brand.spend[channel]?.total ?? 0),
                pointWidth: 72,
                legendIndex: LEGEND_ORDER.indexOf(channel),
            })),
            ...(hasNoDataBrands ? [{
                type: 'column' as const,
                name: 'No Data',
                color: '#BDC2CA',
                data: brands.map(brand => isNoData(brand) ? 1 : 0),
                pointWidth: 72,
                dataLabels: { enabled: false },
                legendIndex: LEGEND_ORDER.length,
            }] : []),
        ];

        return {
            chart: { type: 'column', height: 350 },
            title: { text: undefined },
            credits: { enabled: false },
            xAxis: {
                categories,
                lineColor: '#DEE0E4',
                tickLength: 0,
                labels: {
                    style: {
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        color: '#34363A',
                        fontWeight: '500',
                    },
                },
            },
            yAxis: {
                min: 0,
                max: 100,
                title: { text: '' },
                gridLineColor: '#EBEDEF',
                labels: {
                    format: '{value}%',
                    style: { fontFamily: 'Poppins', fontSize: '11px', color: '#686D75' },
                },
                tickInterval: 20,
            },
            plotOptions: {
                column: {
                    stacking: 'percent',
                    borderWidth: 0,
                    dataLabels: {
                        enabled: true,
                        formatter: function () {
                            if (!this.y) return null as any;
                            return `${Math.round(this.percentage!)}%`;
                        },
                        style: {
                            fontFamily: 'Poppins',
                            fontSize: '11px',
                            fontWeight: '500',
                            color: '#FFFFFF',
                            textOutline: 'none',
                        },
                    },
                },
            },
            legend: {
                align: 'left',
                symbolRadius: 5,
                itemStyle: {
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    fontWeight: '400',
                    color: '#686D75',
                },
            },
            tooltip: {
                useHTML: true,
                shadow: false,
                backgroundColor: '#FFFFFF',
                borderColor: '#DEE0E4',
                style: { fontFamily: 'Poppins', fontSize: '12px' },
                pointFormatter: function () {
                    if (this.series.name === 'No Data') return 'No Data';
                    return `<b>${this.series.name}</b>: ${Math.round((this as any).percentage)}%`;
                },
            },
            series,
        };
    }
}
